import React, { useEffect, useState } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import AddIcon from '@material-ui/icons/Add'
import {
  Box,
  makeStyles,
  IconButton,
  Badge,
  Backdrop,
  Paper,
  Typography,
} from '@material-ui/core'
import {
  matches as matchesModule,
  lineups as lineupsModule,
} from 'redux/modules'
import FieldBackground from 'assets/images/field.jpeg'
import PropTypes from 'prop-types'
import { Translate } from 'react-localize-redux'
import AlertDialog from 'components/AlertDialog'
import useTeamFeature from 'hooks/useTeamFeature'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import LockIcon from '@material-ui/icons/Lock'
import PlayersListPopover from './PlayersListPopover'
import PlayerFieldCard from './PlayerFieldCard'
import AddLineupDialog from './AddLineupDialog'
import LineupEditMode from './LineupEditMode'
import LineupsFilter from './LineupsFilter'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const useStyles = makeStyles((theme) => ({
  fieldWrapper: {
    position: 'relative',
    padding: 8,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.common.white,
  },
  backdrop: {
    position: 'absolute',
    zIndex: theme.zIndex.drawer + 1,
    // Use color with rgba so that the internal content is still visible without opacity being applied to it
    backgroundColor: 'rgba(245, 245, 245, 0.5)',
  },
  fieldImage: {
    width: '100%',
    height: '100%',
  },
  styledPlayer: {
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
  },
  plusIcon: {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.default,
    boxShadow: `0px 2px 6px ${theme.palette.grey[500]}`,
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0px 2px 10px ${theme.palette.grey[500]}`,
    },
  },
  validDrop: {
    border: '2px dashed transparent',
    borderColor: theme.palette.success.main, // Color when hovering over drop area
  },
  invalidDrop: {
    border: '2px dashed transparent',
    borderColor: theme.palette.error.main, // Color when hovering outside drop area
  },
  lockBadgeOnIconButton: {
    '& .MuiBadge-badge': {
      width: 12,
      height: 12,
      minWidth: 12,
      backgroundColor: theme.palette.background.paper,
      transform: 'translate(15px, -15px)',
    },
    '& .MuiBadge-anchorOriginTopRightCircular .MuiSvgIcon-root': {
      fontSize: 10,
    },
  },
}))

const FieldArea = ({
  matchPlayers,
  updateMatchParticipant,
  lineupPositions,
  matchLineup,
  saveMatchLineup,
  updateMatch,
  currentMatchUuid,
  teamUuid,
  updateMatchParticipantsInBulk,
  isEditMode,
  setIsEditMode,
  lineups,
  setLineupPositions,
}) => {
  const classes = useStyles()

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const [fieldPositions, setFieldPositions] = useState([])
  const [customFieldPositions, setCustomFieldPositions] = useState([])
  const [selectedPosition, setSelectedPosition] = useState()
  const [rostersList, setRostersList] = useState([])

  const [isEditModeAlert, setIsEditModeAlert] = useState(false)
  const [addLineupDialogOpen, setAddLineupDialogOpen] = useState(false)
  const featureGuard = useFeatureAccessGuard()

  const { enabled: hasAdvancedMatchFeaturesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.advanced_match_features_enable
  )

  const handleAddLineupDialogClose = () => {
    setAddLineupDialogOpen(false)
  }

  const handleEditModeConfirm = () => {
    setIsEditModeAlert(false)
    setIsEditMode(true)

    const selectedParticipants = matchPlayers.filter((p) => p.participant)

    if (selectedParticipants.length > 0) {
      // Reset all participants x=null,y=null and participant=false to clear the field
      const matchParticipants = matchPlayers.map((p) => {
        return { ...p, position_x: null, position_y: null, participant: false }
      })

      updateMatchParticipantsInBulk(currentMatchUuid, {
        participants: matchParticipants,
      })
    }
  }

  const handleEditFormationClick = () => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    // Show clear field popup only when there are players on the field otherwise just go to edit mode directly
    const selectedParticipants = matchPlayers.filter((p) => p.participant)

    if (selectedParticipants.length > 0) {
      setIsEditModeAlert(true)
    } else {
      setIsEditMode(true)
      handleEditModeConfirm()
    }
  }

  const handleCancel = () => {
    setIsEditMode(false)
  }

  const handleSave = () => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    setAddLineupDialogOpen(true)
  }

  const handlePlusIconClick = (event, position) => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    setAnchorEl(event.currentTarget)
    setSelectedPosition(position)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const handlePlayerSelect = (position, player) => {
    setFieldPositions(
      fieldPositions.map((pos) => {
        if (pos.x === position.x && pos.y === position.y) {
          return { ...pos, player_uuid: player.uuid, player }
        }

        return pos
      })
    )
    setAnchorEl(null)

    updateMatchParticipant(player.matchPlayerUuid, {
      position_x: position.x,
      position_y: position.y,
    })
  }

  const handleClearPosition = (position) => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    setFieldPositions(
      fieldPositions.map((pos) => {
        const temp = { ...pos }
        if (temp.x === position.x && temp.y === position.y) {
          delete temp.player
        }

        return temp
      })
    )
    updateMatchParticipant(position.uuid, {
      position_x: null,
      position_y: null,
    })
  }

  useEffect(() => {
    if (lineupPositions) {
      setFieldPositions(lineupPositions)
    }
  }, [lineupPositions])

  useEffect(() => {
    const { width } = document
      .getElementById('participantsField')
      .getBoundingClientRect()

    document.getElementById('participantsField').style.height = `${
      width * 1.613
    }px`
  }, [])

  const handleSaveLineup = (values) => {
    const requestData = {
      title: values.title,
      positions: customFieldPositions.map((pos) => {
        return { x: +pos.x, y: +pos.y }
      }),
    }

    saveMatchLineup(teamUuid, requestData).then((res) => {
      const { data } = res.response

      handleAddLineupDialogClose()

      // Update match lineup on new custom lineup save.
      // Also with this match lineup will automatically switched to new saved lineup in UI
      updateMatch(currentMatchUuid, {
        lineup_uuid: data.uuid,
        preventToastNotification: true,
      })

      setIsEditMode(false)
    })
  }

  useEffect(() => {
    if (matchLineup && matchLineup.lineup) {
      setCustomFieldPositions(matchLineup.lineup)
    }
  }, [matchLineup])

  useEffect(() => {
    if (matchPlayers.length > 0) {
      setRostersList(matchPlayers.filter((p) => p.in_roster))
    }
  }, [matchPlayers])

  return (
    <>
      {addLineupDialogOpen && (
        <AddLineupDialog
          open={addLineupDialogOpen}
          handleClose={handleAddLineupDialogClose}
          onSubmit={handleSaveLineup}
        />
      )}
      {isEditModeAlert && (
        <AlertDialog
          open={isEditModeAlert}
          onClose={() => setIsEditModeAlert(false)}
          onSuccess={handleEditModeConfirm}
          onCancel={() => setIsEditModeAlert(false)}
          title={<Translate id="matches.change-lineup" />}
          subTitle={<Translate id="matches.confirm-lineup-change-message" />}
          submitButtonLabel={<Translate id="button.ok" />}
          cancelButtonLabel={<Translate id="button.close" />}
        />
      )}
      <LineupsFilter
        lineups={lineups}
        lineupPositions={lineupPositions}
        setLineupPositions={setLineupPositions}
        isEditMode={isEditMode}
        handleEdit={handleEditFormationClick}
        handleSave={handleSave}
        handleCancel={handleCancel}
      />

      {isEditMode ? (
        <LineupEditMode
          matchLineup={matchLineup}
          customFieldPositions={customFieldPositions}
          setCustomFieldPositions={setCustomFieldPositions}
        />
      ) : (
        <Box className={classes.fieldWrapper}>
          {/* Show overlay if roster list is empty */}
          {rostersList.length === 0 && (
            <Backdrop className={classes.backdrop} open>
              <Box
                component={Paper}
                p={3}
                borderRadius={8}
                style={{
                  width: 300,
                }}
                textAlign="center"
              >
                <Typography variant="h4">
                  <Translate id="matches.choose-roster" />
                </Typography>

                <Typography variant="body1">
                  <Translate id="matches.add-roster-text" />
                </Typography>
              </Box>
            </Backdrop>
          )}
          <Box id="participantsField">
            <img
              src={FieldBackground}
              alt="Pitch/Field"
              className={classes.fieldImage}
            />

            {fieldPositions.map((position) => (
              <Box
                key={`${position.x}${position.y}`}
                className={classes.styledPlayer}
                style={{
                  left: `${position.x}%`,
                  top: `${position.y}%`,
                }}
              >
                {position.matchPlayer ? (
                  <PlayerFieldCard
                    handleClearPosition={handleClearPosition}
                    position={position.matchPlayer}
                  />
                ) : (
                  <IconButton
                    className={classes.plusIcon}
                    onClick={(event) => handlePlusIconClick(event, position)}
                    aria-describedby={position.id}
                  >
                    <Badge
                      className={
                        !hasAdvancedMatchFeaturesEnabled
                          ? classes.lockBadgeOnIconButton
                          : ''
                      }
                      badgeContent={
                        !hasAdvancedMatchFeaturesEnabled ? (
                          <LockIcon color="primary" fontSize="small" />
                        ) : null
                      }
                      overlap="circular"
                    >
                      <AddIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                )}
              </Box>
            ))}
            {open && selectedPosition && (
              <PlayersListPopover
                open={open}
                players={rostersList}
                handlePlayerSelect={handlePlayerSelect}
                handlePopoverClose={handlePopoverClose}
                position={selectedPosition}
                anchorEl={anchorEl}
                handleClose={handlePopoverClose}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

FieldArea.propTypes = {
  matchPlayers: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  lineupPositions: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  updateMatchParticipant: PropTypes.func.isRequired,
  matchLineup: PropTypes.shape().isRequired,
  saveMatchLineup: PropTypes.func.isRequired,
  updateMatch: PropTypes.func.isRequired,
  currentMatchUuid: PropTypes.string.isRequired,
  teamUuid: PropTypes.string.isRequired,
  updateMatchParticipantsInBulk: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  setIsEditMode: PropTypes.func.isRequired,
  lineups: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  setLineupPositions: PropTypes.func.isRequired,
}

export default compose(
  withRouter,
  connect(
    ({ matches: { current }, team }) => ({
      currentMatch: current,
      matchPlayers: current.relationships.participants,
      currentMatchUuid: current.uuid,
      teamUuid: team.uuid,
    }),
    {
      deleteMatch: matchesModule.deleteMatch,
      updateMatchParticipant: matchesModule.updateMatchParticipant,
      saveMatchLineup: lineupsModule.saveMatchLineup,
      updateMatch: matchesModule.updateMatch,
      updateMatchParticipantsInBulk:
        matchesModule.updateMatchParticipantsInBulk,
    }
  )
)(FieldArea)
